import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getLotHistoryDescription } from "@/components/ui/lot-number-details";
import { LotRowLabel } from "@/components/ui/lot-number-select";
import { DynamicSelectItem } from "@/components/ui/select";
import { type LotNumberHistory, getLotNumbers } from "@/api/provider/scribe/notes";
import { ShipmentLot } from "@/query/medications/medications";
import { useTenantStore } from "@/stores/tenant-store";
export function preventDoseLotEntryDismiss(event: KeyboardEvent) {
  if (document.activeElement instanceof HTMLInputElement && document.activeElement.closest("[data-dose-lot-new-entry]")) event.preventDefault();
}

/** Keep the existing form encoding private; the editor always works with a collection. */
export function parseDoseLotReferences(value?: string): string[] {
  const codes = (value ?? "").split("\n").map(code => code.trim()).filter(Boolean);
  return Array.from(new Map(codes.map(code => [code.toLowerCase(), code])).values());
}
export function useDoseLotHistory({
  medicationId,
  locationId,
  isLoadingInventory = false
}: {
  medicationId?: string | null;
  locationId?: string | null;
  isLoadingInventory?: boolean;
}) {
  const tenant = useTenantStore(state => state.currentTenant);
  return useQuery({
    queryKey: ["lot-numbers", tenant, medicationId ?? null, locationId ?? null],
    queryFn: async () => (await getLotNumbers({
      medicationId: medicationId ?? undefined,
      locationId: locationId ?? undefined
    })) ?? [],
    enabled: !!medicationId && !isLoadingInventory,
    staleTime: 0,
    refetchOnMount: "always"
  });
}
function DoseLotReferenceRow(t0) {
  const $ = _c(22);
  const {
    code,
    selected,
    onToggle,
    history,
    medicationName
  } = t0;
  const id = useId();
  const t1 = `Select lot ${code}`;
  let t2;
  let t3;
  if ($[0] !== code || $[1] !== onToggle) {
    t2 = () => onToggle(code);
    t3 = event => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      onToggle(code);
    };
    $[0] = code;
    $[1] = onToggle;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== id || $[5] !== selected || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <Checkbox id={id} checked={selected} data-lot-picker-choice={true} aria-label={t1} className="mt-0.5 border-border bg-background" onCheckedChange={t2} onKeyDown={t3} />;
    $[4] = id;
    $[5] = selected;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = history?.medicationName ?? medicationName;
  let t6;
  if ($[10] !== code || $[11] !== t5) {
    t6 = <LotRowLabel lotNumber={code} productName={t5} wrap={true} />;
    $[10] = code;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== history) {
    t7 = history && <span className="block text-caption text-foreground-secondary">{getLotHistoryDescription(history)}</span>;
    $[13] = history;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== id || $[16] !== t6 || $[17] !== t7) {
    t8 = <Label htmlFor={id} className="min-w-0 flex-1 break-words font-normal">{t6}{t7}</Label>;
    $[15] = id;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t8) {
    t9 = <div className="border-b border-border py-2 last:border-b-0"><div className="flex items-start gap-3 px-2 py-1">{t4}{t8}</div></div>;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
export function DoseLotReferenceRows(t0) {
  const $ = _c(11);
  const {
    choices,
    onToggle,
    history,
    medicationName
  } = t0;
  let t1;
  if ($[0] !== choices || $[1] !== history || $[2] !== medicationName || $[3] !== onToggle) {
    let t2;
    if ($[5] !== history || $[6] !== medicationName || $[7] !== onToggle) {
      t2 = choice => <DoseLotReferenceRow key={choice.code.toLowerCase()} {...choice} onToggle={onToggle} history={history.find(entry => entry.lotNumber.toLowerCase() === choice.code.toLowerCase())} medicationName={medicationName} />;
      $[5] = history;
      $[6] = medicationName;
      $[7] = onToggle;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = choices.map(t2);
    $[0] = choices;
    $[1] = history;
    $[2] = medicationName;
    $[3] = onToggle;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <>{t1}</>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

/** Uses the inventory search; custom codes never cover physical stock quantities. */
export function DoseLotReferenceOptions(t0) {
  const $ = _c(48);
  const {
    medicationId,
    medicationName,
    shipments,
    search,
    references,
    onAdd,
    onSelectShipment,
    isLoadingInventory: t1,
    onPendingChange,
    historyQuery
  } = t0;
  const isLoadingInventory = t1 === undefined ? false : t1;
  const [notice, setNotice] = useState();
  const [entryActive, setEntryActive] = useState(false);
  const [entryKey, setEntryKey] = useState(0);
  let t2;
  if ($[0] !== onPendingChange) {
    t2 = () => {
      setEntryActive(false);
      onPendingChange(false);
    };
    $[0] = onPendingChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const finishEntry = t2;
  const {
    data: t3,
    isPending,
    isError,
    refetch
  } = historyQuery;
  let add;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== medicationName || $[3] !== onAdd || $[4] !== onSelectShipment || $[5] !== references || $[6] !== search || $[7] !== shipments || $[8] !== t3) {
    const history = t3 === undefined ? [] : t3;
    let t9;
    if ($[15] !== onAdd || $[16] !== onSelectShipment || $[17] !== shipments) {
      t9 = raw => {
        const code = raw.trim();
        if (!code) {
          return;
        }
        setNotice(undefined);
        const matches = shipments.filter(lot => lot.lotNumber?.trim().toLowerCase() === code.toLowerCase());
        if (matches.length > 1) {
          setNotice("Multiple inventory entries have this lot number. Use the supplier, location, and expiry to select the correct lot above.");
          return;
        }
        if (matches[0]) {
          onSelectShipment(matches[0].id);
          return;
        }
        onAdd(code);
      };
      $[15] = onAdd;
      $[16] = onSelectShipment;
      $[17] = shipments;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    add = t9;
    const visible = Array.from(new Map(history.map(_temp2)).values()).filter(entry_0 => entry_0.lotNumber.toLowerCase().includes(search.trim().toLowerCase()) && !references.some(value => value.toLowerCase() === entry_0.lotNumber.toLowerCase()) && !shipments.some(lot_0 => lot_0.lotNumber?.trim().toLowerCase() === entry_0.lotNumber.trim().toLowerCase()));
    t6 = "Lot history and new lot numbers";
    t7 = "space-y-2";
    t8 = visible.length > 0 && <h3 className="text-caption-strong">Previously used lot numbers</h3>;
    t4 = "max-h-32 overflow-y-auto";
    t5 = visible.map(entry_1 => <Button key={entry_1.lotNumber} type="button" variant="ghost" className="h-auto min-w-0 w-full flex-col items-start whitespace-normal text-left" onClick={() => add(entry_1.lotNumber)}><LotRowLabel lotNumber={entry_1.lotNumber} productName={entry_1.medicationName ?? medicationName} wrap={true} /><span className="w-full whitespace-normal break-words text-caption text-foreground-secondary">{getLotHistoryDescription(entry_1)}</span></Button>);
    $[2] = medicationName;
    $[3] = onAdd;
    $[4] = onSelectShipment;
    $[5] = references;
    $[6] = search;
    $[7] = shipments;
    $[8] = t3;
    $[9] = add;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    add = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t5) {
    t9 = <div className={t4}>{t5}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isLoadingInventory || $[23] !== isPending || $[24] !== medicationId) {
    t10 = medicationId && isPending && !isLoadingInventory && <p className="text-caption text-foreground-secondary">Loading previously used lot numbers…</p>;
    $[22] = isLoadingInventory;
    $[23] = isPending;
    $[24] = medicationId;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== isError || $[27] !== refetch) {
    t11 = isError && <p role="alert" className="text-caption text-status-warning-text">Could not load lot history.{" "}<Button type="button" variant="ghost" size="sm" onClick={() => void refetch()}>Retry</Button></p>;
    $[26] = isError;
    $[27] = refetch;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== add || $[30] !== entryActive || $[31] !== entryKey || $[32] !== finishEntry || $[33] !== isLoadingInventory || $[34] !== onPendingChange || $[35] !== search) {
    t12 = !isLoadingInventory && <div data-dose-lot-new-entry={true} onKeyDownCapture={event => {
      if (event.key === "Escape") {
        finishEntry();
      }
    }}><DynamicSelectItem key={entryKey} label="Add new lot number" addPlaceholder="Enter new lot number..." initialValue={search} onInputChange={() => {
        setEntryActive(true);
        onPendingChange(true);
      }} onClose={finishEntry} onAddOption={value_0 => {
        add(value_0);
        finishEntry();
      }} />{entryActive && <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-caption text-foreground-secondary">Add this lot or cancel the entry before saving.</p><Button type="button" variant="ghost" size="sm" onClick={() => {
          finishEntry();
          setEntryKey(_temp3);
        }}>Cancel new lot</Button></div>}</div>;
    $[29] = add;
    $[30] = entryActive;
    $[31] = entryKey;
    $[32] = finishEntry;
    $[33] = isLoadingInventory;
    $[34] = onPendingChange;
    $[35] = search;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== notice) {
    t13 = notice && <p role="status" className="text-caption text-status-warning-text">{notice}</p>;
    $[37] = notice;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t14 = <section aria-label={t6} className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}</section>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
}
function _temp3(previous) {
  return previous + 1;
}
function _temp2(entry) {
  return [entry.lotNumber.toLowerCase(), entry];
}